import { seamApiLtsVersion } from '../../../lib/lts-version.js';
import { getAuthHeadersForClientSessionToken, warnOnInsecureuserIdentifierKey, } from './auth.js';
import { createClient } from './client.js';
import { isSeamHttpOptionsWithApiKey, isSeamHttpOptionsWithClient, isSeamHttpOptionsWithClientSessionToken, isSeamHttpOptionsWithConsoleSessionToken, isSeamHttpOptionsWithPersonalAccessToken, SeamHttpInvalidOptionsError, } from './options.js';
import { limitToSeamHttpRequestOptions, parseOptions } from './parse-options.js';
import { SeamHttpAccessCodes, SeamHttpAcs, SeamHttpActionAttempts, SeamHttpClientSessions, SeamHttpConnectedAccounts, SeamHttpConnectWebviews, SeamHttpDevices, SeamHttpEvents, SeamHttpLocks, SeamHttpNetworks, SeamHttpNoiseSensors, SeamHttpPhones, SeamHttpThermostats, SeamHttpUserIdentities, SeamHttpWebhooks, SeamHttpWorkspaces, } from './routes/index.js';
export class SeamHttp {
    constructor(apiKeyOrOptions = {}) {
        this.ltsVersion = seamApiLtsVersion;
        const options = parseOptions(apiKeyOrOptions);
        this.client = 'client' in options ? options.client : createClient(options);
        this.defaults = limitToSeamHttpRequestOptions(options);
    }
    static fromClient(client, options = {}) {
        const constructorOptions = { ...options, client };
        if (!isSeamHttpOptionsWithClient(constructorOptions)) {
            throw new SeamHttpInvalidOptionsError('Missing client');
        }
        return new SeamHttp(constructorOptions);
    }
    static fromApiKey(apiKey, options = {}) {
        const constructorOptions = { ...options, apiKey };
        if (!isSeamHttpOptionsWithApiKey(constructorOptions)) {
            throw new SeamHttpInvalidOptionsError('Missing apiKey');
        }
        return new SeamHttp(constructorOptions);
    }
    static fromClientSessionToken(clientSessionToken, options = {}) {
        const constructorOptions = { ...options, clientSessionToken };
        if (!isSeamHttpOptionsWithClientSessionToken(constructorOptions)) {
            throw new SeamHttpInvalidOptionsError('Missing clientSessionToken');
        }
        return new SeamHttp(constructorOptions);
    }
    static async fromPublishableKey(publishableKey, userIdentifierKey, options = {}) {
        warnOnInsecureuserIdentifierKey(userIdentifierKey);
        const clientOptions = parseOptions({ ...options, publishableKey });
        if (isSeamHttpOptionsWithClient(clientOptions)) {
            throw new SeamHttpInvalidOptionsError('The client option cannot be used with SeamHttp.fromPublishableKey');
        }
        const client = createClient(clientOptions);
        const clientSessions = SeamHttpClientSessions.fromClient(client);
        const { token } = await clientSessions.getOrCreate({
            user_identifier_key: userIdentifierKey,
        });
        return SeamHttp.fromClientSessionToken(token, options);
    }
    static fromConsoleSessionToken(consoleSessionToken, workspaceId, options = {}) {
        const constructorOptions = { ...options, consoleSessionToken, workspaceId };
        if (!isSeamHttpOptionsWithConsoleSessionToken(constructorOptions)) {
            throw new SeamHttpInvalidOptionsError('Missing consoleSessionToken or workspaceId');
        }
        return new SeamHttp(constructorOptions);
    }
    static fromPersonalAccessToken(personalAccessToken, workspaceId, options = {}) {
        const constructorOptions = { ...options, personalAccessToken, workspaceId };
        if (!isSeamHttpOptionsWithPersonalAccessToken(constructorOptions)) {
            throw new SeamHttpInvalidOptionsError('Missing personalAccessToken or workspaceId');
        }
        return new SeamHttp(constructorOptions);
    }
    async updateClientSessionToken(clientSessionToken) {
        const { headers } = this.client.defaults;
        const authHeaders = getAuthHeadersForClientSessionToken({
            clientSessionToken,
        });
        for (const key of Object.keys(authHeaders)) {
            if (headers[key] == null) {
                throw new Error('Cannot update a clientSessionToken on a client created without a clientSessionToken');
            }
        }
        this.client.defaults.headers = { ...headers, ...authHeaders };
        const clientSessions = SeamHttpClientSessions.fromClient(this.client);
        await clientSessions.get();
    }
    get accessCodes() {
        return SeamHttpAccessCodes.fromClient(this.client, this.defaults);
    }
    get acs() {
        return SeamHttpAcs.fromClient(this.client, this.defaults);
    }
    get actionAttempts() {
        return SeamHttpActionAttempts.fromClient(this.client, this.defaults);
    }
    get clientSessions() {
        return SeamHttpClientSessions.fromClient(this.client, this.defaults);
    }
    get connectedAccounts() {
        return SeamHttpConnectedAccounts.fromClient(this.client, this.defaults);
    }
    get connectWebviews() {
        return SeamHttpConnectWebviews.fromClient(this.client, this.defaults);
    }
    get devices() {
        return SeamHttpDevices.fromClient(this.client, this.defaults);
    }
    get events() {
        return SeamHttpEvents.fromClient(this.client, this.defaults);
    }
    get locks() {
        return SeamHttpLocks.fromClient(this.client, this.defaults);
    }
    get networks() {
        return SeamHttpNetworks.fromClient(this.client, this.defaults);
    }
    get noiseSensors() {
        return SeamHttpNoiseSensors.fromClient(this.client, this.defaults);
    }
    get phones() {
        return SeamHttpPhones.fromClient(this.client, this.defaults);
    }
    get thermostats() {
        return SeamHttpThermostats.fromClient(this.client, this.defaults);
    }
    get userIdentities() {
        return SeamHttpUserIdentities.fromClient(this.client, this.defaults);
    }
    get webhooks() {
        return SeamHttpWebhooks.fromClient(this.client, this.defaults);
    }
    get workspaces() {
        return SeamHttpWorkspaces.fromClient(this.client, this.defaults);
    }
}
SeamHttp.ltsVersion = seamApiLtsVersion;
//# sourceMappingURL=seam-http.js.map